import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import {
  IconBell,
  IconHelp,
  IconUser,
  IconUsers,
  IconSchool,
  IconChecks,
  IconLicense,
  IconFileText,
  IconNotebook,
  IconChalkboard,
  IconFileReport,
  IconAlertTriangle,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import { EWebNotificationType, EWebNotificationCategory } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import {
  clearWebNotifications,
  getWebNotificationList,
  markWebNotificationRead,
} from '@/service/notificationServices';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IWebNotification } from '@/types';
import { formatLastActive } from '@/utils';

import Button from '../Button/Button';
import {
  CATEGORY_LOCALES,
  CATEGORY_TAG_STYLES,
  NOTIFICATION_TYPE_LOCALES,
  NOTIFICATION_TYPE_TO_CATEGORY,
} from './config';

const LIMIT = 10;

interface INotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  onUnreadCountChange?: (count: number) => void;
}

const NotificationIcon = ({
  category,
}: {
  category: EWebNotificationCategory;
}) => {
  const iconWrapper = 'w-6';

  switch (category) {
    case EWebNotificationCategory.SYSTEM:
      return (
        <div className={iconWrapper}>
          <IconAlertTriangle size={24} className="text-error-secondary" />
        </div>
      );
    case EWebNotificationCategory.LICENSE:
      return (
        <div className={iconWrapper}>
          <IconLicense size={24} />
        </div>
      );
    case EWebNotificationCategory.CHILD:
    case EWebNotificationCategory.STUDENT:
      return (
        <div className={iconWrapper}>
          <UserAvatarList user="" className="w-6! h-6! min-w-6! text-[10px]!" />
        </div>
      );
    case EWebNotificationCategory.LESSON:
      return (
        <div className={iconWrapper}>
          <IconNotebook size={24} />
        </div>
      );
    case EWebNotificationCategory.REPORT:
      return (
        <div className={iconWrapper}>
          <IconFileReport size={24} />
        </div>
      );
    case EWebNotificationCategory.FAQ:
      return (
        <div className={iconWrapper}>
          <IconHelp size={24} />
        </div>
      );
    case EWebNotificationCategory.POLICY:
      return (
        <div className={iconWrapper}>
          <IconFileText size={24} />
        </div>
      );
    case EWebNotificationCategory.GAME:
      return (
        <div className={iconWrapper}>
          <IconDeviceGamepad2 size={24} />
        </div>
      );
    case EWebNotificationCategory.CLASS:
      return (
        <div className={iconWrapper}>
          <IconChalkboard size={24} />
        </div>
      );
    case EWebNotificationCategory.TEACHER:
      return (
        <div className={iconWrapper}>
          <IconUsers size={24} />
        </div>
      );
    case EWebNotificationCategory.SCHOOL:
      return (
        <div className={iconWrapper}>
          <IconSchool size={24} />
        </div>
      );
    case EWebNotificationCategory.PARENT:
      return (
        <div className={iconWrapper}>
          <IconUser size={24} />
        </div>
      );
    default:
      return (
        <div className={iconWrapper}>
          <IconBell size={24} />
        </div>
      );
  }
};

const NotificationDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  onUnreadCountChange,
}: INotificationDropdownProps) => {
  const { formatText } = useFormatText();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const [notifications, setNotifications] = useState<IWebNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Use a ref for the loading guard to avoid stale closures in useCallback
  const loadingRef = useRef(false);

  const handleClearAll = useCallback(async () => {
    if (notifications.length === 0) return;
    const response = await clearWebNotifications();
    if (response) {
      setNotifications([]);
      setUnreadCount(0);
      setOffset(0);
      setHasMore(false);
      onUnreadCountChange?.(0);
      fetchNotifications(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, onUnreadCountChange]);

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      const response = await markWebNotificationRead(id);
      if (response) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((prev) => {
          const newCount = Math.max(0, prev - 1);
          onUnreadCountChange?.(newCount);
          return newCount;
        });
      }
    },
    [onUnreadCountChange]
  );

  const fetchNotifications = useCallback(
    async (currentOffset: number, reset = false) => {
      // Use ref-based guard to prevent concurrent fetches without stale closure issues
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      const response = await getWebNotificationList({
        limit: LIMIT,
        offset: currentOffset,
      });
      loadingRef.current = false;
      setLoading(false);

      if (response?.status) {
        const newItems: IWebNotification[] = response.data || [];
        const totalCount: number = response.total || 0;
        const newUnreadCount: number = response.unread_count || 0;
        setUnreadCount(newUnreadCount);
        onUnreadCountChange?.(newUnreadCount);
        setNotifications((prev) => (reset ? newItems : [...prev, ...newItems]));
        const nextOffset = currentOffset + newItems.length;
        setOffset(nextOffset);
        setHasMore(nextOffset < totalCount);
      }
    },
    [onUnreadCountChange]
  );
  // Load initial data when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Reset the loading ref guard so a fresh fetch can always proceed
      loadingRef.current = false;
      setNotifications([]);
      setOffset(0);
      setHasMore(true);
      fetchNotifications(0, true);
    }
  }, [isOpen, fetchNotifications]);

  // Infinite scroll handler
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listEl;
      if (
        scrollHeight - scrollTop - clientHeight < 80 &&
        hasMore &&
        !loadingRef.current
      ) {
        fetchNotifications(offset);
      }
    };

    listEl.addEventListener('scroll', handleScroll);
    return () => listEl.removeEventListener('scroll', handleScroll);
  }, [hasMore, offset, fetchNotifications]);

  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      const dropdownWidth = 450;

      setPosition({
        top: rect.bottom + scrollY + 8,
        left: isRTL
          ? rect.left + scrollX
          : rect.right + scrollX - dropdownWidth,
      });
    }
  }, [isOpen, isRTL, anchorRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        anchorRef.current &&
        !anchorRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-9999 bg-white border border-neutral-light-200 rounded-2xl shadow-lg w-md overflow-hidden flex flex-col"
      style={{
        top: position.top,
        left: position.left,
        maxHeight: 'calc(100vh - 250px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-neutral-light-200">
        <div className="flex justify-start items-center gap-2">
          <h3 className="text-[20px] text-neutral-primary">
            {formatText('header-notification.title')}
          </h3>
          <span className="flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-neutral-light-200 text-xs font-semibold text-neutral-primary">
            {unreadCount === 0
              ? '0'
              : unreadCount < 10
                ? `0${unreadCount}`
                : unreadCount}
          </span>
        </div>
        <Button
          variant="link"
          label={formatText('header-notification.clear-all')}
          onClick={handleClearAll}
          disabled={notifications.length === 0}
          className="text-lg!"
        />
      </div>

      {/* Notification List */}
      <div ref={listRef} className="overflow-y-auto flex-1 p-6">
        <div className="flex flex-col gap-3">
          {notifications?.map((item) => (
            <div
              key={item.id}
              className={`relative flex gap-4 p-4 border rounded-2xl transition cursor-pointer ${
                item.is_read
                  ? 'border-neutral-light-200 bg-neutral-light-50 hover:bg-neutral-light-100'
                  : 'border-neutral-light-200 bg-primary-light-50 hover:bg-primary-light-100'
              }`}
            >
              <NotificationIcon
                category={
                  item.notification_type !== null
                    ? NOTIFICATION_TYPE_TO_CATEGORY[
                        item.notification_type as EWebNotificationType
                      ]
                    : EWebNotificationCategory.SYSTEM
                }
              />
              <div className="flex flex-col gap-1 min-w-0 w-full">
                <div className="flex items-center justify-between gap-4 w-full">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-neutral-primary leading-snug">
                      {item.is_global
                        ? item.title
                        : item.notification_type !== null
                          ? formatText(
                              `header-notification.titles.${NOTIFICATION_TYPE_LOCALES[item.notification_type as EWebNotificationType]}`
                            )
                          : ''}
                    </span>
                    {!item.is_global &&
                      item.notification_type !== null &&
                      CATEGORY_LOCALES[
                        NOTIFICATION_TYPE_TO_CATEGORY[item.notification_type]
                      ] && (
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${CATEGORY_TAG_STYLES[NOTIFICATION_TYPE_TO_CATEGORY[item.notification_type as EWebNotificationType]] || ''}`}
                        >
                          {formatText(
                            `header-notification.category.${CATEGORY_LOCALES[NOTIFICATION_TYPE_TO_CATEGORY[item.notification_type as EWebNotificationType]]}`
                          )}
                        </span>
                      )}
                  </div>
                  {!item.is_read && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(item.id);
                      }}
                      className="p-1 rounded-full hover:bg-primary-light-100 transition cursor-pointer"
                      title="Mark as read"
                    >
                      <IconChecks
                        size={18}
                        className="text-primary-secondary"
                      />
                    </button>
                  )}
                </div>
                <p className="text-sm text-neutral-secondary leading-snug">
                  {item.is_global ? (
                    item.description
                  ) : (
                    <>
                      <span className="font-semibold">
                        {item?.notification_data?.action_by ||
                          item?.notification_data?.created_by}
                      </span>{' '}
                      {item.notification_type !== null &&
                        formatText(
                          `header-notification.messages.${NOTIFICATION_TYPE_LOCALES[item.notification_type as EWebNotificationType]}`
                        )}{' '}
                      <span className="font-bold">
                        {item?.notification_data?.name}
                      </span>
                    </>
                  )}
                </p>
                <span className="text-sm font-light text-neutral-seconedary mt-2">
                  {formatLastActive(Number(item?.created_at), formatText)}
                </span>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-center py-3">
              <span className="text-sm text-neutral-secondary">
                {formatText('header-notification.loading')}
              </span>
            </div>
          )}

          {!loading && !hasMore && notifications.length > 0 && (
            <div className="flex justify-center py-3">
              <span className="text-sm text-neutral-secondary">
                {formatText('header-notification.no-more')}
              </span>
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <img
                src="/assets/svg/notification-empty.svg"
                alt="No notifications"
                className="w-[150px] h-[150px]"
              />
              <div className="text-center">
                <h4 className="text-lg font-semibold text-neutral-primary">
                  {formatText('header-notification.empty-title')}
                </h4>
                <p className="text-sm text-neutral-secondary mt-1">
                  {formatText('header-notification.empty-desc')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationDropdown;
